// externals
import React, { useMemo } from 'react';
import type { TaxDetails } from '@makemydeal/dr-dash-store/types/types';

// libraries
import type { paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { SimpleTable } from '@interstate/components/SimpleTable';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';

// components
import TableHead from './TableHead';
import TableBody from './TableBody';
import TaxItemRateField from './TaxItemRateField';
import { DefaultFallback } from '../../common/taxesAndFeesComponents';
import TaxItemAmountField from './TaxItemAmountField';

// styles
import { StyledTableCell, StyledTableCellBaseWithoutBorder } from './TaxCard.style';

// consts/enums
import { DATA_DENSITY_LARGE } from '../../common/taxesAndFeesConsts';
import { PRIMARY_CLASS, ADDITIONAL_CLASS, TAXBASETABLECOLUMNS } from '../constants';
import { getTaxDetails } from './helpers';

const TaxItemSummaryDetailed: React.FC<{
    taxItem: paymentServicesTypes.TaxItem;
    handleChange: (breakdownType: 'amount' | 'rate') => (e: InterstateOnChangeEvent<TextInputEventValue>, keyName: string) => void;
    isTotalTaxRateOverThreshold?: boolean;
    taxOverrideEnabled?: boolean;
}> = ({ taxItem, handleChange, isTotalTaxRateOverThreshold, taxOverrideEnabled }) => {
    const editableTaxClasses = [PRIMARY_CLASS];
    const shouldDisableFields = taxOverrideEnabled;

    const taxDetails = useMemo(() => getTaxDetails(taxItem, shouldDisableFields), [taxItem, shouldDisableFields]);
    editableTaxClasses.push(ADDITIONAL_CLASS);

    taxDetails.forEach((taxDetail) => {
        taxDetail.hasError = isTotalTaxRateOverThreshold;
    });

    const renderRow = (detail: TaxDetails, index: number) => (
        <React.Fragment key={`detailed-cell-${index}-${detail.name}-${detail.amount}`}>
            <StyledTableCellBaseWithoutBorder $alignContent="flex-start" $lineHeight="2.8125rem">
                <StyledTableCell $textAlign="left" data-testid={`detailed-table-cell-${detail.name}`}>
                    {detail.name}
                </StyledTableCell>
            </StyledTableCellBaseWithoutBorder>
            <StyledTableCellBaseWithoutBorder
                $alignContent="flex-start"
                {...(!detail.isRateEditable && detail.rate == undefined && { $lineHeight: '2.125rem' })}
            >
                {detail.isRateEditable ? (
                    <StyledTableCell data-testid={`detailed-table-cell-${detail.rate}-${detail.key}`}>
                        <TaxItemRateField data={detail} handleBlur={handleChange('rate')} />
                    </StyledTableCell>
                ) : (
                    <StyledTableCell
                        $textAlign="left"
                        data-testid={`detailed-table-cell-${detail.rate}`}
                        $minWidth="8rem"
                        $marginTop="1rem"
                    >
                        {detail.rate == undefined ? <DefaultFallback /> : `${detail.rate}%`}
                    </StyledTableCell>
                )}
            </StyledTableCellBaseWithoutBorder>
            <StyledTableCellBaseWithoutBorder
                key={`detail-cell-amount-${index}-${detail.key}-${taxItem.taxName}`}
                $alignContent="flex-start"
                {...(!detail.isAmountEditable && detail.rate === undefined && { $lineHeight: '2.125rem' })}
            >
                {detail.isAmountEditable ? (
                    <StyledTableCell data-testid={`detailed-table-cell-${detail.amount}-${detail.key}`}>
                        <TaxItemAmountField data={detail} handleBlur={handleChange('amount')} />
                    </StyledTableCell>
                ) : (
                    <StyledTableCell
                        $textAlign="right"
                        data-testid={`detailed-table-cell-${detail.amount}`}
                        $minWidth="9rem"
                        $marginTop="1rem"
                    >
                        {detail.rate == undefined ? <DefaultFallback /> : formatDollarsAndCents(detail.amount)}
                    </StyledTableCell>
                )}
            </StyledTableCellBaseWithoutBorder>
        </React.Fragment>
    );

    return (
        <SimpleTable
            data-testid="expanded-table-data-testid"
            dataDensity={DATA_DENSITY_LARGE}
            background="white"
            bordered={false}
            hover={false}
            className="tax-item-summary-detailed-table"
            sx={{ overflowX: 'unset', backgroundColor: 'inherit', '& thead tr th': { backgroundColor: 'inherit!important' } }}
        >
            <TableHead columns={TAXBASETABLECOLUMNS} />
            <TableBody data={taxDetails} renderRow={renderRow} />
        </SimpleTable>
    );
};

export default TaxItemSummaryDetailed;
